import { useBlocker } from '@remix-run/react'
// eslint-disable-next-line no-restricted-imports
import type { useLocation } from '@remix-run/react'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import { debug } from '~/lib/log'
import { useIsLanding } from '~/navigation/atoms'

type Location = ReturnType<typeof useLocation>
export type WillBeUnmounted = (next: Location) => boolean
type BeforeNavigate = (next: Location) => Promise<any>

const beforeNavigates$ = atom<BeforeNavigate[]>([])

// Remix の useBlocker 機能を駆使した unmount 前に dialog をアニメーション付きて閉じるための仕組み
// useOpenCloseAnimation と連携して機能する
export function ApplyBeforeNavigates() {
  const beforeNavigates = useAtomValue(beforeNavigates$)
  const nextLocationRef = useRef<Location | undefined>(undefined)
  const started = useRef(false)

  // Remix の blocker はコンポーネントツリー全体でただ一つしか使えないので Provider で管理する (root.tsx でのみ使う想定)
  // pushstate / popstate 双方のイベントを拾って、常に画面遷移を止める
  const blocker = useBlocker(({ historyAction, nextLocation }) => {
    nextLocationRef.current = nextLocation
    // リストで検索モーダル出して stack 書き換えで表示されなくなるときも閉じるアニメーションをさせたい
    return historyAction === 'POP' || historyAction === 'PUSH'
  })

  // blocker が画面遷移を止めていたら、登録済の beforeNavigate を全部実行してから blocker を解除する
  // 各モーダルでは useWaitCloseAnimation 経由で、閉じるアニメーションを実行する async 関数を登録
  useEffect(() => {
    if (blocker.state === 'blocked' && nextLocationRef.current != undefined && !started.current) {
      const nextLocation = nextLocationRef.current
      started.current = true
      Promise.all(beforeNavigates.map((f) => f(nextLocation))).then(() => {
        // 直前で再度状態チェック
        // proceed を呼び出したところで blocker の状態と不整合を起こしていたら Remix が例外を投げるが、
        // Promise 内なので unhandledrejection で Sentry に通知されるだけで、ユーザーに影響はない。
        // そもそも画面遷移が block されていなければ proceed しても意味はまったくない。
        started.current = false
        try {
          if (blocker.state === 'blocked') {
            blocker.proceed()
          }
        } catch (e) {
          // useBlocker は最近 unstable が外れたばかりの Remix (React Router) の機能なせいか、
          // 直前に blocker state チェックを加えても browser back を2連打すると例外が投げられることがあるので抑制
          if (
            process.env.NODE_ENV !== 'production' ||
            !(e instanceof Error) ||
            !/Invalid blocker state transition/.test(e.message)
          ) {
            throw e
          }
        }
      })
    }
    return () => {
      started.current = false
    }
  }, [beforeNavigates, blocker])

  return null
}

/** 画面遷移前に実行したい処理を Promise として作る関数を登録する */
export function useBeforeNavigate(beforeNavigate: BeforeNavigate) {
  const set = useSetAtom(beforeNavigates$)
  useEffect(() => {
    set((current) => [...current, beforeNavigate])
    // 標準的な listener 実装で unmount 時に登録を解除している
    return () => {
      set((current) => current.filter((b) => b !== beforeNavigate))
    }
  }, [beforeNavigate, set])
}

type Params = {
  ref: RefObject<HTMLElement>
  willBeUnmounted: WillBeUnmounted
  openKeyframes: Keyframe[]
  openOptions?: KeyframeAnimationOptions
  closeKeyframes?: Keyframe[]
  closeOptions?: KeyframeAnimationOptions
  /**
   * アニメーション後に transform を削除するかどうか
   *
   * transform が残ると ikyu-login の fixed の基準になって問題が起きるため
   * https://github.com/ikyucom/macaron-frontend/issues/5116#issuecomment-2553350979
   */
  removeTransformAfterAnimation?: boolean
}

export function useOpenCloseAnimation({
  ref,
  willBeUnmounted,
  openKeyframes,
  openOptions = {
    duration: 400,
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    fill: 'forwards',
  },
  // デフォルトは単純な逆回し、複雑な keyframes が必要なら利用側で
  closeKeyframes = [...openKeyframes].reverse(),
  closeOptions = {
    duration: 300,
    easing: 'ease-out',
    fill: 'forwards',
  },
  removeTransformAfterAnimation,
}: Params) {
  // ランディング時から mount されていたときは開始アニメーションしない
  const opened = useRef(useIsLanding())
  // useEffect でコンポーネントの mount 時に開くアニメーションを実行する
  useEffect(() => {
    // SSR では何もしない
    if (typeof window === 'undefined') {
      return
    }
    if (ref.current === null) {
      return
    }
    debug('[navigate] useOpenCloseAnimation', {
      opened: opened.current,
      openKeyframes,
      openOptions,
    })
    if (opened.current) {
      return
    }
    opened.current = true
    const div = ref.current

    debug('[navigate] useOpenCloseAnimation - start open animation', div)
    const animation = div.animate(openKeyframes, openOptions)
    animation.finished.then(() => {
      try {
        animation.commitStyles()
        animation.cancel()
      } catch (e) {
        // 悪さしないので握り潰す
        // InvalidStateError: The object is in an invalid state.
        if (!(e instanceof Error && e.name === 'InvalidStateError')) {
          throw e
        }
      } finally {
        if (removeTransformAfterAnimation) {
          div.style.transform = ''
        }
      }
    })
  }, [openKeyframes, openOptions, ref, removeTransformAfterAnimation])

  // 遷移後の状態で unmount 対象の dialog のときは閉じるアニメーションを待つ Promise を作って返す関数を作っている
  const beforeUnmount: BeforeNavigate = useCallback(
    async (next) => {
      if (!willBeUnmounted(next)) {
        return
      }
      if (ref.current === null) {
        return
      }
      const element = ref.current
      const animation = element.animate(closeKeyframes, closeOptions)
      await animation.finished
      element.style.display = 'none'
    },
    [willBeUnmounted, ref, closeKeyframes, closeOptions]
  )
  useBeforeNavigate(beforeUnmount)
}
